import React from 'react';
import { CustomParagraph, CustomSlateElement } from '../../../../models/slate-editor';
import classes from './FirstCommentPreview.module.scss';
import classNames from 'classnames';
import { SocialProfile } from '../../../../components/socialProfiles/SocialProfile';
import { BoxNotification } from '../../../../components/containers/BoxNotification';
import { SocialProfileWithBoards } from '../../../../hooks/useCategoriesProfilesWithBoards';
import { serializeTextToHtml } from '../../TextEditor/helpers/serializer';
import DOMPurify from 'dompurify';
import { Network } from '../../../../models/graphql';
import { profilesHelpers } from '../../../Settings/Profiles/Profiles.helpers';

export type FirstCommentPreviewViewProps = {
  //
  socialProfile: SocialProfileWithBoards;
  firstComment: CustomParagraph[];
};

const serializeTextToArrayOfJSX = (text: CustomSlateElement[]): JSX.Element[] => {
  const parser = new DOMParser();
  const result = serializeTextToHtml(text, undefined, 'no-b-m');
  const doc = parser.parseFromString(result, 'text/html');
  const paragraphs = Array.from(doc.querySelectorAll('p'));

  const values = paragraphs.map(paragraph => ({
    className: paragraph.getAttribute('class'),
    text: DOMPurify.sanitize(paragraph.innerText.trim())
  }));
  let jsx: JSX.Element[] = [];
  for (let i = 0; i < values.length; i++) {
    jsx = [
      ...jsx,
      <p className={values[i].className || ''} key={`paragraph-${i + 1}`}>
        {values[i].text || '\u00A0'}
      </p>
    ];
  }
  // ^^^ '\u00A0' is an unicode represantiton of &nbsp; and in order to have a visible empty paragraph we need to render this
  return jsx;
};

export const FirstCommentPreviewView = (props: FirstCommentPreviewViewProps): JSX.Element => {
  const { socialProfile, firstComment } = props;
  const baseSocialProfileProps = {
    id: socialProfile.id,
    name: socialProfile.displayName
  };
  let mappedSocialProfile;
  const apiType = socialProfile.__typename === 'InstagramSocialProfile' ? socialProfile.apiType : undefined;
  const provider = profilesHelpers.mapOAuthFromTypename(socialProfile.__typename, apiType);

  switch (socialProfile.__typename) {
    case 'FacebookSocialProfile':
      mappedSocialProfile = {
        ...baseSocialProfileProps,
        provider,
        network: Network.FACEBOOK,
        type: socialProfile.type
      };
      break;
    case 'InstagramSocialProfile':
      mappedSocialProfile = {
        ...baseSocialProfileProps,
        provider,
        network: Network.INSTAGRAM,
        type: socialProfile.type
      };
      break;
    case 'LinkedInSocialProfile':
      mappedSocialProfile = {
        ...baseSocialProfileProps,
        provider,
        network: Network.LINKEDIN,
        type: socialProfile.type
      };
      break;
    case 'GoogleSocialProfile':
      mappedSocialProfile = {
        ...baseSocialProfileProps,
        provider,
        network: Network.GOOGLE,
        type: ''
      };
      break;
    case 'TwitterSocialProfile':
      mappedSocialProfile = {
        ...baseSocialProfileProps,
        provider,
        network: Network.TWITTER,
        type: ''
      };
      break;
    case 'PinterestSocialProfile':
      mappedSocialProfile = {
        ...baseSocialProfileProps,
        provider,
        network: Network.PINTEREST,
        type: ''
      };
      break;
    default:
      mappedSocialProfile = null;
      break;
  }
  if (!mappedSocialProfile) throw new Error('Invalid Social Profile');
  return (
    <div className={classNames(classes.FirstCommentWrapper, 'grid-x')}>
      <div className={'cell shrink'}>
        <SocialProfile
          socialProfile={mappedSocialProfile}
          disconnected={!socialProfile.connected}
          sublabel={false}
          size={'medium'}
          label={false}
          image={socialProfile.photoUrl}
        />
      </div>
      <div className={classNames('cell', 'auto', classes.FirstCommentText)}>
        <BoxNotification type={'info'}>{serializeTextToArrayOfJSX(firstComment)}</BoxNotification>
      </div>
    </div>
  );
};
